import React, { memo, FC } from "react"
import {
    GetUpdatedBookerDocument,
    UpdatePaymentMethodInput,
    useUpdateSepaPaymentMethodMutation,
} from "../../generated/graphql"
import { Formik } from "formik"
import { SepaDebitValidationSchema } from "../CreateBooking/Steps/paymentMethodForms/SepaDebitValidationSchema"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import LayoutForm from "../../views/LayoutForm/LayoutForm"
import { Button, Card, Form, Input, message } from "antd"
import { definedInputValue } from "../common/helpers"
import { Link, useHistory } from "react-router-dom"
import { URL_ACCOUNT } from "../../models/url"
import { margin } from "../../styles/layout"
import { blue } from "@ant-design/colors"
import css from "./SepaData.module.css"
import LoginContainer from "../../views/LoginContainer/LoginContainer"
import { safeStorage } from "../../helpers/safeStorage"

type SepaDataInput = {
    sepaAccountholder?: string
    iban?: string
    bank?: string
    bic?: string
}

type SepaDebitEditFormProps = {
    pm: SepaDataInput
    paymentMethodId: string
    bookerId: string
    fromUrl?: { pathname: string }
}

const SepaDebitEditForm: FC<SepaDebitEditFormProps> = ({ pm, paymentMethodId, fromUrl, bookerId }) => {
    const [loading, setLoading] = React.useState(false)
    const history = useHistory()
    const [updateSepaPaymentMethod] = useUpdateSepaPaymentMethodMutation({
        refetchQueries: ["GetUpdatedBooker", { query: GetUpdatedBookerDocument, variables: { id: bookerId } }],
        awaitRefetchQueries: true,
    })

    const updateBookersPaymentMethodInLocalStorage = (
        paymentMethodId: string,
        updatedValues: UpdatePaymentMethodInput
    ) => {
        // Get booker from localStorage
        const bookerString = safeStorage.getItem("booker")
        if (!bookerString) return

        try {
            // Parse booker JSON
            const booker = JSON.parse(bookerString)

            // Find and update the payment method
            const paymentMethodIndex = booker.paymentMethods.findIndex((pm: any) => pm.id === paymentMethodId)

            if (paymentMethodIndex === -1) return

            // Update the payment method values
            booker.paymentMethods[paymentMethodIndex] = {
                ...booker.paymentMethods[paymentMethodIndex],
                iban: updatedValues.iban,
                bank: updatedValues.bank,
                bic: updatedValues.bic,
                sepaAccountholder: updatedValues.sepaAccountholder,
            }

            // Save back to localStorage
            safeStorage.setItem("booker", JSON.stringify(booker))
        } catch (error) {
            console.error("Error updating payment method in localStorage:", error)
        }
    }

    const handleSubmit = (updatePaymentMethodInput: UpdatePaymentMethodInput) => {
        setLoading(true)
        try {
            updateSepaPaymentMethod({
                variables: { input: updatePaymentMethodInput },
            })
                .then((res) => {
                    console.log("res", res)
                    updateBookersPaymentMethodInLocalStorage(paymentMethodId, updatePaymentMethodInput)
                    message.success("Lastschriftdaten erfolgreich aktualisiert")
                    history.push(fromUrl?.pathname ?? URL_ACCOUNT)
                })
                .catch((error) => {
                    console.log("error", error)
                    message.error("Fehler beim Aktualisieren der Lastschriftdaten")
                })
        } catch (error) {
            console.log("error", error)
            message.error("Fehler beim Aktualisieren der Lastschriftdaten")
        } finally {
            console.log("finally")
            setLoading(false)
        }
    }

    if (!pm) return <div>Payment method not found</div>
    console.log("pm", pm)

    const initialValues: UpdatePaymentMethodInput = {
        id: paymentMethodId ?? "",
        sepaAccountholder: pm.sepaAccountholder,
        iban: pm.iban,
        bank: pm.bank,
        bic: pm.bic,
    }

    return (
        <Formik<UpdatePaymentMethodInput>
            initialValues={initialValues}
            onSubmit={(values) => {
                console.log("values", values)
                handleSubmit(values)
            }}
            validationSchema={SepaDebitValidationSchema}
        >
            {(formikProps) => {
                const values = formikProps.values
                console.log("formikProps.values", formikProps.values)

                const { handleTextInputChange, handleIbanInputChange, help, handleBlur } = createInputHelpers(formikProps)

                return (
                    <LoginContainer
                        size='s'
                        title={
                            <div className={css.editCardTitle}>
                                <div style={{ paddingLeft: margin.min, paddingRight: margin.min }}>
                                    Lastschriftdaten bearbeiten
                                </div>
                                {/* <Popconfirm title='Entgültig löschen?' cancelText='Abrechen'>
                                    <Button icon={<DeleteOutlined />} type='text' style={{ color: red[3] }} />
                                </Popconfirm> */}
                            </div>
                        }
                        style={{ padding: 0 }}
                        // style={{ border: "2px dotted red" }}
                        content={
                            <Card
                                bodyStyle={{ padding: margin.s }}
                                loading={loading}
                                actions={[
                                    <Link
                                        to={{
                                            pathname: fromUrl?.pathname ?? URL_ACCOUNT,
                                        }}
                                        key='edit'
                                    >
                                        <Button type='text' danger key='cancel' style={{ fontWeight: 600 }}>
                                            Abbrechen
                                        </Button>
                                    </Link>,

                                    <Button
                                        type='text'
                                        key='save'
                                        style={{ fontWeight: 600, color: blue[5] }}
                                        htmlType='submit'
                                        onClick={() => formikProps.submitForm()}
                                    >
                                        Speichern
                                    </Button>,
                                ]}
                            >
                                <LayoutForm columns={1}>
                                    <Form.Item label='Kontoinhaber' help={help("sepaAccountholder")} required>
                                        <Input
                                            value={definedInputValue(values.sepaAccountholder)}
                                            onChange={handleTextInputChange("sepaAccountholder")}
                                            onBlur={handleBlur("sepaAccountholder")}
                                        />
                                    </Form.Item>
                                    <Form.Item label='IBAN' help={help("iban")} required>
                                        <Input
                                            value={definedInputValue(values.iban)}
                                            onChange={handleIbanInputChange("iban")}
                                            onBlur={handleBlur("iban")}
                                        />
                                    </Form.Item>
                                    <Form.Item label='Bank' help={help("bank")} required>
                                        <Input
                                            value={definedInputValue(values.bank)}
                                            onChange={handleTextInputChange("bank")}
                                            onBlur={handleBlur("bank")}
                                        />
                                    </Form.Item>
                                    <Form.Item label='Bic' help={help("bic")} required>
                                        <Input
                                            value={definedInputValue(values.bic)}
                                            onChange={handleTextInputChange("bic")}
                                            onBlur={handleBlur("bic")}
                                        />
                                    </Form.Item>
                                </LayoutForm>
                            </Card>
                        }
                    />
                )
            }}
        </Formik>
    )
}

export default memo(SepaDebitEditForm)
